import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { toast, Toaster } from 'sonner';
import { CheckCircle, Clock, Buildings, Warning, X, Users } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const REQUEST_TYPES = [
  { v: 'late_arrival', l: 'Late Arrival', desc: 'Will arrive after 10:30 AM' },
  { v: 'early_leave', l: 'Early Leave', desc: 'Need to leave before 7:30 PM' },
  { v: 'custom_hours', l: 'Custom Working Hours', desc: 'e.g. 8:00 AM – 5:00 PM' },
  { v: 'short_permission', l: 'Short Permission', desc: 'Quick break (10 / 15 / 20 mins)' },
  { v: 'wfh', l: 'Work From Home', desc: 'Full day or partial WFH' },
  { v: 'other', l: 'Other', desc: 'Anything else (specify in reason)' },
];

const DURATION_OPTIONS = [10, 15, 20, 30, 45, 60, 90, 120];
const todayStr = () => new Date().toISOString().slice(0, 10);

export default function PermissionRequestPublicPage() {
  const [employees, setEmployees] = useState([]);
  const [dirLoading, setDirLoading] = useState(true);
  const [dirError, setDirError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [showTeamPicker, setShowTeamPicker] = useState(false);
  const [form, setForm] = useState({
    employee_id: '',
    emp_id: '',
    request_type: 'late_arrival',
    date: todayStr(),
    requested_start: '',
    requested_end: '',
    duration_minutes: 15,
    reason: '',
    team_members: [],
  });

  const loadDir = useCallback(async () => {
    setDirLoading(true);
    setDirError('');
    try {
      const r = await axios.get(`${API_URL}/api/public/employees`, { timeout: 15000 });
      const list = r.data || [];
      setEmployees(list);
      if (list.length === 0) setDirError('Employee directory is empty. Please contact admin.');
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail || e.message;
      console.error('Failed to load /api/public/employees', e);
      if (status === 404) setDirError(`Endpoint not deployed yet on server (404). Ask admin to restart the backend after the latest git pull.`);
      else if (e.code === 'ECONNABORTED') setDirError('Server timed out. Try again or check your connection.');
      else setDirError(`Could not load employee list (${status || 'network'}): ${detail}. Tap Retry.`);
    }
    setDirLoading(false);
  }, []);

  useEffect(() => { loadDir(); }, [loadDir]);

  const needsTime = form.request_type === 'custom_hours' || form.request_type === 'late_arrival' || form.request_type === 'early_leave';
  const isShort = form.request_type === 'short_permission';

  const toggleTeamMember = (id) => {
    setForm((f) => {
      const cur = new Set(f.team_members || []);
      if (cur.has(id)) cur.delete(id); else cur.add(id);
      return { ...f, team_members: Array.from(cur) };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.employee_id) return toast.error('Please select your name');
    if (!form.emp_id.trim()) return toast.error('Please enter your Employee ID');
    if (!form.reason.trim()) return toast.error('Please provide a reason');
    if (isShort && !form.duration_minutes) return toast.error('Please pick a duration');
    setSubmitting(true);
    try {
      const r = await axios.post(`${API_URL}/api/public/permission-requests`, form);
      const submitterName = employees.find((e) => e.id === form.employee_id)?.name || form.emp_id;
      const teamNames = (form.team_members || []).map((id) => employees.find((e) => e.id === id)?.name).filter(Boolean);
      setSubmitted({
        ...form,
        id: (r.data.ids && r.data.ids[0]) || r.data.id,
        count: r.data.count || 1,
        submitter_name: submitterName,
        team_names: teamNames,
      });
      toast.success(r.data.count > 1 ? `${r.data.count} requests submitted for the group!` : 'Permission request submitted!');
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div style={{ minHeight: '100vh', background: '#f3f4f6' }} className="flex items-center justify-center p-4">
        <Toaster richColors />
        <Card className="p-8 max-w-md w-full text-center">
          <CheckCircle size={56} weight="fill" className="mx-auto text-emerald-500 mb-3" />
          <h2 className="text-2xl font-bold mb-2" style={{ color: '#1a2e4a' }}>
            {submitted.count > 1 ? `${submitted.count} Requests Submitted` : 'Request Submitted'}
          </h2>
          <p className="text-sm text-gray-600 mb-2">
            Thanks, <strong>{submitted.submitter_name}</strong>! Your <strong>{REQUEST_TYPES.find((t) => t.v === submitted.request_type)?.l}</strong> request for <strong>{submitted.date}</strong> has been sent to admin.
          </p>
          {submitted.team_names && submitted.team_names.length > 0 && (
            <div className="bg-blue-50 rounded p-3 text-left text-xs my-3">
              <div className="font-bold text-blue-800 mb-1">Group request includes:</div>
              <ul className="list-disc pl-4 text-blue-900">
                <li>{submitted.submitter_name} (you)</li>
                {submitted.team_names.map((n) => <li key={n}>{n}</li>)}
              </ul>
            </div>
          )}
          <div className="text-xs text-gray-400 mb-4">Reference ID: {(submitted.id || '').slice(-8) || '—'}</div>
          <Button
            onClick={() => {
              setSubmitted(null);
              setForm({ employee_id: '', emp_id: '', request_type: 'late_arrival', date: todayStr(), requested_start: '', requested_end: '', duration_minutes: 15, reason: '', team_members: [] });
            }}
            data-testid="submit-another-btn"
          >
            Submit Another Request
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0d1117 0%, #1a2e4a 100%)' }} className="flex items-center justify-center p-4">
      <Toaster richColors />
      <Card className="p-6 sm:p-8 max-w-xl w-full" data-testid="permission-public-form">
        <div className="text-center mb-5">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-2" style={{ background: '#1D9E75' }}>
            <Buildings size={28} weight="fill" className="text-white" />
          </div>
          <h1 className="text-2xl font-bold" style={{ color: '#1a2e4a' }}>Permission Request</h1>
          <p className="text-xs text-gray-500 mt-1">Dextra Technologies</p>
        </div>

        {/* Office hours reminder */}
        <div className="mb-4 px-3 py-2 rounded-md border border-amber-200 bg-amber-50 flex items-center gap-2 text-xs">
          <Clock size={14} weight="bold" className="text-amber-700 flex-shrink-0" />
          <div className="text-amber-900">
            <strong>Office Hours:</strong> 10:30 AM – 7:30 PM &nbsp;•&nbsp; <span className="text-amber-700">Submit any deviation here.</span>
          </div>
        </div>

        {dirError && (
          <div className="mb-3 px-3 py-2 rounded-md border border-rose-200 bg-rose-50 flex items-center justify-between text-xs">
            <div className="flex items-center gap-2 text-rose-900">
              <Warning size={14} weight="fill" className="text-rose-600" />
              {dirError}
            </div>
            <Button size="sm" variant="outline" onClick={loadDir} disabled={dirLoading} data-testid="retry-dir-btn">
              {dirLoading ? 'Loading…' : 'Retry'}
            </Button>
          </div>
        )}

        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Your Name *</Label>
              <select
                className="w-full border rounded px-3 py-2 text-sm bg-white disabled:bg-gray-100"
                value={form.employee_id}
                onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
                required
                disabled={dirLoading || !!dirError}
                data-testid="employee-select"
              >
                <option value="">{dirLoading ? 'Loading employees…' : '— select your name —'}</option>
                {employees.map((e) => (
                  <option key={e.id} value={e.id}>{e.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs">Employee ID *</Label>
              <Input
                placeholder="e.g. EMP001"
                value={form.emp_id}
                onChange={(e) => setForm({ ...form, emp_id: e.target.value })}
                required
                data-testid="emp-id-input"
              />
            </div>
          </div>

          {/* Team members (group request) */}
          <div className="border rounded p-3 bg-slate-50">
            <button
              type="button"
              onClick={() => setShowTeamPicker((s) => !s)}
              className="w-full flex items-center justify-between text-xs font-semibold text-slate-700"
              data-testid="toggle-team-picker"
            >
              <span className="flex items-center gap-1.5"><Users size={14} weight="duotone" /> Add Team Members (optional)</span>
              <span className="text-slate-400">{showTeamPicker ? 'Hide' : 'Show'}</span>
            </button>
            {(form.team_members || []).length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2" data-testid="selected-team-chips">
                {form.team_members.map((id) => {
                  const e = employees.find((x) => x.id === id);
                  return (
                    <span key={id} className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">
                      {e?.name || id.slice(-6)}
                      <button type="button" onClick={() => toggleTeamMember(id)} className="ml-1 hover:text-rose-600"><X size={10} /></button>
                    </span>
                  );
                })}
              </div>
            )}
            {showTeamPicker && employees.length > 0 && (
              <div className="mt-2 max-h-40 overflow-y-auto border bg-white rounded p-2 text-xs">
                {employees.filter((e) => e.id !== form.employee_id).map((e) => (
                  <label key={e.id} className="flex items-center gap-2 py-1 cursor-pointer hover:bg-slate-50 px-1 rounded">
                    <input
                      type="checkbox"
                      checked={(form.team_members || []).includes(e.id)}
                      onChange={() => toggleTeamMember(e.id)}
                      data-testid={`team-cb-${e.emp_id}`}
                    />
                    <span className="flex-1">{e.name}</span>
                    <span className="text-[9px] text-gray-400 font-mono">{e.emp_id}</span>
                  </label>
                ))}
              </div>
            )}
            <p className="text-[10px] text-slate-500 mt-1">
              Submitting for multiple people? Pick them here — one request is created per person.
            </p>
          </div>

          <div>
            <Label className="text-xs">Request Type *</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
              {REQUEST_TYPES.map((t) => (
                <button
                  type="button"
                  key={t.v}
                  onClick={() => setForm({ ...form, request_type: t.v })}
                  className={`text-left p-2 rounded border-2 transition-all ${form.request_type === t.v ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:border-gray-300'}`}
                  data-testid={`type-${t.v}`}
                >
                  <div className="text-xs font-semibold" style={{ color: form.request_type === t.v ? '#15803d' : '#1a2e4a' }}>{t.l}</div>
                  <div className="text-[10px] text-gray-500 mt-0.5">{t.desc}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Date *</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required data-testid="date-input" />
            </div>
            {isShort ? (
              <div className="col-span-2">
                <Label className="text-xs flex items-center gap-1"><Clock size={11} /> Duration *</Label>
                <select
                  className="w-full border rounded px-3 py-2 text-sm bg-white"
                  value={form.duration_minutes}
                  onChange={(e) => setForm({ ...form, duration_minutes: parseInt(e.target.value, 10) })}
                  data-testid="duration-select"
                >
                  {DURATION_OPTIONS.map((d) => <option key={d} value={d}>{d} minutes</option>)}
                </select>
              </div>
            ) : needsTime ? (
              <>
                <div>
                  <Label className="text-xs flex items-center gap-1"><Clock size={11} /> Start</Label>
                  <Input type="time" value={form.requested_start} onChange={(e) => setForm({ ...form, requested_start: e.target.value })} data-testid="start-input" />
                </div>
                <div>
                  <Label className="text-xs flex items-center gap-1"><Clock size={11} /> End</Label>
                  <Input type="time" value={form.requested_end} onChange={(e) => setForm({ ...form, requested_end: e.target.value })} data-testid="end-input" />
                </div>
              </>
            ) : null}
          </div>

          <div>
            <Label className="text-xs">Reason *</Label>
            <textarea
              className="w-full border rounded px-3 py-2 text-sm min-h-[80px]"
              placeholder="Brief explanation (e.g. doctor appointment, family commitment, project deadline, etc.)"
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              required
              data-testid="reason-input"
            />
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={submitting || dirLoading || !!dirError}
            data-testid="submit-btn"
          >
            {submitting ? 'Submitting…'
              : (form.team_members || []).length > 0
                ? `Submit for ${(form.team_members.length + 1)} People`
                : 'Submit Permission Request'}
          </Button>

          <p className="text-[10px] text-gray-400 text-center pt-2">
            Your request will be reviewed by admin. You can check the status with your manager.
          </p>
        </form>
      </Card>
    </div>
  );
}
